import { createContext, useContext, useReducer, useCallback } from 'react';
import { SettingsManager } from '@atlas/core';

import { betslipTabIndex, myBetsTabIndex } from 'common/Betslip/constants';

const initialState = {
  betIdToEdit: null,
  hasChangesEditedBet: false,
  isOpenPopup: false,
  tabIndex: betslipTabIndex,
  // trigger will show popup which suggest user to save edit bet changes
  showEditBetCancelPopup: false,
  isOrderBet: false,
  selectedBetTypeIndex: 0,
};

export const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'SET_STATE': {
      return {
        ...state,
        ...payload,
      };
    }
    case 'SET_BET_ID_TO_EDIT': {
      const { betId } = payload;
      return { ...state, betIdToEdit: betId };
    }
    case 'OPEN_EDIT_BET_CANCEL_POPUP': {
      return {
        ...state,
        showEditBetCancelPopup: true,
      };
    }
    case 'CLOSE_EDIT_BET_CANCEL_POPUP': {
      return {
        ...state,
        showEditBetCancelPopup: false,
      };
    }
    case 'CLOSE_FORCE_EDIT_MODE': {
      return {
        ...state,
        betIdToEdit: initialState.betIdToEdit,
        showEditBetCancelPopup: initialState.showEditBetCancelPopup,
        hasChangesEditedBet: initialState.hasChangesEditedBet,
      };
    }
    case 'CLOSE_EDIT_MODE': {
      const { hasChangesEditedBet } = state;

      if (hasChangesEditedBet) {
        return { ...state, showEditBetCancelPopup: true };
      }

      return { ...state, betIdToEdit: initialState.betIdToEdit };
    }
    case 'TOGGLE_POPUP': {
      const { isOpenPopup } = state;
      return {
        ...state,
        isOpenPopup: !isOpenPopup,
      };
    }
    case 'SET_TAB_INDEX': {
      const { tabIndex } = payload;
      const { betIdToEdit } = state;

      // user cannot change tab if one is editing bet (will show popup instead)
      if (betIdToEdit) {
        return { ...state };
      }

      if (tabIndex === myBetsTabIndex) {
        // FIXME: The quick bet can not save its own state,
        //  so it's better to disable it to prevent bugs
        SettingsManager.updateSettings({ quickBet: false });
      }

      return {
        ...state,
        tabIndex,
      };
    }
    default: {
      return state;
    }
  }
};

export const getLocalBetslipState = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setState = useCallback(payload => {
    dispatch({ type: 'SET_STATE', payload });
  }, []);

  const setBetIdToEdit = useCallback((betId = null) => {
    dispatch({ type: 'SET_BET_ID_TO_EDIT', payload: { betId } });
  }, []);

  const setIsOpenPopup = useCallback(
    isOpenPopup => {
      setState({ isOpenPopup });
    },
    [setState],
  );

  const onCloseBetslipPopup = useCallback(() => {
    setState({ isOpenPopup: false });
  }, [setState]);

  const setIsOrderBet = useCallback(
    isOrderBet => {
      setState({ isOrderBet });
    },
    [setState],
  );

  const togglePopup = useCallback(() => {
    dispatch({ type: 'TOGGLE_POPUP', payload: {} });
  }, []);

  const setTabIndex = useCallback(tabIndex => {
    dispatch({ type: 'SET_TAB_INDEX', payload: { tabIndex } });
  }, []);

  const setShowEditBetCancelPopup = useCallback(
    showEditBetCancelPopup => {
      setState({ showEditBetCancelPopup });
    },
    [setState],
  );

  const setHasChangesEditedBet = useCallback(
    hasChangesEditedBet => {
      setState({ hasChangesEditedBet });
    },
    [setState],
  );

  const setSelectedBetTypeIndex = useCallback(
    selectedBetTypeIndex => {
      setState({ selectedBetTypeIndex });
    },
    [setState],
  );

  const openEditBetCancelPopup = useCallback(() => {
    dispatch({ type: 'OPEN_EDIT_BET_CANCEL_POPUP' });
  }, []);

  const closeEditBetCancelPopup = useCallback(() => {
    dispatch({ type: 'CLOSE_EDIT_BET_CANCEL_POPUP' });
  }, []);

  const closeForceEditMode = useCallback(() => {
    dispatch({ type: 'CLOSE_FORCE_EDIT_MODE' });
  }, []);

  const closeEditMode = useCallback(() => {
    dispatch({ type: 'CLOSE_EDIT_MODE' });
  }, []);

  return {
    ...state,
    setBetIdToEdit,
    setIsOpenPopup,
    onCloseBetslipPopup,
    togglePopup,
    setTabIndex,
    setShowEditBetCancelPopup,
    setIsOrderBet,
    setState,
    setHasChangesEditedBet,
    setSelectedBetTypeIndex,
    closeForceEditMode,
    openEditBetCancelPopup,
    closeEditBetCancelPopup,
    closeEditMode,
  };
};

const LocalBetslipContext = createContext();

export const useLocalBetslip = () => useContext(LocalBetslipContext);

export const LocalBetslipContextProvider = LocalBetslipContext.Provider;
